import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Mail, Check, ShieldCheck, Coins, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { SIG, applySignature, fetchSignedPreview, canApplySignature, canProceedFromPreview, isSubmitDisabled, SIGNATURE_SEND_ERROR_CODES } from "@/lib/signatureFlow";
import { SignatureAuthorizationPanel, SignedConfirmation } from "@/components/SignatureAuthorizationPanel";

const STEPS = ["Recipient", "Return Address", "Preview", "Options", "Cost", "Approve"];
const money = (c) => `$${((c ?? 0) / 100).toFixed(2)}`;

const BUREAU_RECIPIENTS = {
  equifax: { name: "Equifax Information Services LLC", address_line1: "P.O. Box 740256", city: "Atlanta", state: "GA", zip_code: "30374" },
  experian: { name: "Experian", address_line1: "P.O. Box 4500", city: "Allen", state: "TX", zip_code: "75013" },
  transunion: { name: "TransUnion LLC Consumer Dispute Center", address_line1: "P.O. Box 2000", city: "Chester", state: "PA", zip_code: "19016" },
};

// Letter types that are mailed to a creditor/furnisher/collector, not a bureau.
// For these, the recipient address must be entered manually — the member knows
// where to send the letter; we don't maintain a canonical directory.
const NON_BUREAU_LETTER_TYPES = ["furnisher", "debt_validation", "mov", "follow_up", "arbitration"];

// CFPB complaints are filed through the online CFPB portal, not by postal mail.
// We refuse to open the mailing wizard for these documents and route the member
// to the dedicated CFPB Builder flow.
const CFPB_LETTER_TYPE = "cfpb";

// Safely extract the first creditor name from a possibly-missing/malformed
// doc.selections array. Returns "" when nothing usable is present.
function extractCreditorName(doc) {
  try {
    const sel = doc && doc.selections;
    if (!Array.isArray(sel) || sel.length === 0) return "";
    const first = sel[0];
    if (!first || typeof first !== "object") return "";
    const name = first.creditor;
    return typeof name === "string" ? name.trim() : "";
  } catch {
    return "";
  }
}

function uuid() { return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`; }

// Two top-level First-Class classes exposed to the member.
const MAIL_CLASSES = [
  {
    id: "standard",
    label: "First-Class Standard",
    tagline: "Regular First-Class delivery; no signature confirmation.",
    startingAt: "Starts at $3.56",
    mail_type: "usps_first_class",
    extra_service: null,
  },
  {
    id: "certified",
    label: "First-Class Certified — Tracked",
    tagline: "Includes proof of mailing, USPS tracking, and delivery confirmation.",
    startingAt: "Adds $6.95",
    mail_type: "usps_first_class",
    extra_service: "certified",
  },
];

export default function MailLetterDialog({ open, onOpenChange, doc, onSent }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [to, setTo] = useState({ name: "", address_line1: "", address_line2: "", city: "", state: "", zip_code: "", country: "US" });
  const [from, setFrom] = useState({ name: "", address_line1: "", address_line2: "", city: "", state: "", zip_code: "", country: "US" });
  const [verifying, setVerifying] = useState(false);
  const [fromVerification, setFromVerification] = useState(null);
  const [toVerifying, setToVerifying] = useState(false);
  const [toVerification, setToVerification] = useState(null);
  const [mailClassId, setMailClassId] = useState("standard");
  const [returnReceipt, setReturnReceipt] = useState(false);
  const [color, setColor] = useState(false);
  const [doubleSided, setDoubleSided] = useState(true);
  const [options, setOptions] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [showBreakdownCost, setShowBreakdownCost] = useState(true);
  const [showBreakdownApprove, setShowBreakdownApprove] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  // Signature is applied BEFORE the final preview; name + timestamp come from the server only.
  const [sigState, setSigState] = useState(SIG.CHECKING);
  const [sigStatus, setSigStatus] = useState({ signature_name: "", profile_name_ok: false });
  const [sigInfo, setSigInfo] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [signError, setSignError] = useState(null);
  const [preview, setPreview] = useState({ state: "idle" });
  const [submitting, setSubmitting] = useState(false);
  const [idem] = useState(uuid());
  const [identityReady, setIdentityReady] = useState(null);
  const [identityConfirmed, setIdentityConfirmed] = useState(false);
  // Price-change UX state (2026-02-25). When the server returns a structured
  // 409 price_changed, we surface a distinct panel with previous vs updated
  // totals and require an explicit "Accept updated price" click before we
  // resubmit. Acceptance auto-resets whenever any price-affecting selection
  // changes so the user can't accidentally confirm a stale delta.
  const [priceChange, setPriceChange] = useState(null); // { previous_cents, new_cents, pages }
  const [sendError, setSendError] = useState(null); // { kind, message } — kind ∈ price_changed | insufficient_balance | address | missing_identity | technical
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(0); setAuthorized(false); setFromVerification(null); setToVerification(null); setReturnReceipt(false); setMailClassId("standard"); setIdentityConfirmed(false);
    setPriceChange(null); setSendError(null);
    setSigInfo(null); setConsentChecked(false); setSignError(null); setPreview({ state: "idle" });
    if (doc?.id) loadSignatureStatus();
    // P1 (2026-10-02): recipient initialization per letter_type.
    // - bureau (equifax/experian/transunion) → canonical directory
    // - CFPB → do not mail; dialog will show a block panel at step 0
    // - non-bureau (furnisher/debt_validation/mov/follow_up/arbitration) →
    //   leave empty, but prefill `name` from the first selection's creditor
    //   when available, and show a prominent explainer at step 0.
    const bureau = BUREAU_RECIPIENTS[doc?.letter_type] || BUREAU_RECIPIENTS[doc?.library?.recipient_bureau];
    const libRecipient = doc?.library?.facts?.recipient;
    if (bureau) {
      setTo({ ...bureau, address_line2: "", country: "US" });
    } else if (libRecipient?.address_line1) {
      setTo({ address_line2: "", country: "US", ...libRecipient });
    } else {
      const prefilledName = extractCreditorName(doc);
      setTo({ name: prefilledName, address_line1: "", address_line2: "", city: "", state: "", zip_code: "", country: "US" });
    }
    setFrom({
      name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
      address_line1: user?.mailing_address || "", address_line2: "",
      city: user?.city || "", state: user?.state || "", zip_code: user?.zip_code || "", country: "US",
    });
    api.get("/mailings/options").then(r => setOptions(r.data)).catch(() => { });
    api.get("/identity-documents").then(r => setIdentityReady(r.data)).catch(() => setIdentityReady({ ready_for_mailing: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc, user]);

  // Reset price-change acceptance whenever ANY price-affecting selection changes.
  // Also clears the sendError banner so a stale message doesn't linger.
  useEffect(() => {
    setPriceChange(null);
    setSendError((prev) => (prev && prev.kind === "price_changed" ? null : prev));
  }, [mailClassId, returnReceipt, color, doubleSided, doc?.id, to.address_line1, to.zip_code, from.address_line1, from.zip_code]);

  // Auto-scroll the scrollable body to the top on every step transition so mobile
  // users always see the top of the new step and the persistent footer stays
  // reachable without scrolling.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step]);

  const selectedClass = useMemo(() => MAIL_CLASSES.find(c => c.id === mailClassId) || MAIL_CLASSES[0], [mailClassId]);

  const resolvedExtraService = useMemo(() => {
    if (selectedClass.id !== "certified") return null;
    return returnReceipt ? "certified_return_receipt" : "certified";
  }, [selectedClass, returnReceipt]);

  const verifyReturn = async () => {
    setVerifying(true);
    try {
      const r = await api.post("/mailings/verify-address", from);
      setFromVerification(r.data);
      if (r.data.deliverability === "deliverable") toast.success("Return address verified");
      else toast.warning(`Deliverability: ${r.data.deliverability}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setVerifying(false); }
  };

  const acceptRecommendedFrom = () => { if (fromVerification?.recommended) { setFrom(fromVerification.recommended); toast.success("Applied recommended address"); } };

  const verifyTo = async () => {
    setToVerifying(true);
    try {
      const r = await api.post("/mailings/verify-address", to);
      setToVerification(r.data);
      if (r.data.deliverability === "deliverable") toast.success("Recipient address verified");
      else toast.warning(`Deliverability: ${r.data.deliverability}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setToVerifying(false); }
  };

  const acceptRecommendedTo = () => { if (toVerification?.recommended) { setTo(toVerification.recommended); toast.success("Applied recommended address"); } };

  const loadQuote = async () => {
    if (!doc?.id) return;
    setQuoteLoading(true);
    try {
      const r = await api.post("/mailings/quote", {
        mail_type: selectedClass.mail_type,
        extra_service: resolvedExtraService,
        color,
        double_sided: doubleSided,
        document_id: doc.id,
      });
      setQuote(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail && typeof detail === "object" && detail.code === "missing_identity") {
        // Quote endpoint now enforces the same identity gate as Preview / Send.
        // Surface the missing-identity state so the client shows the checklist
        // instead of a broken "Quote failed" toast.
        setSendError({ kind: "missing_identity", message: detail.message || "Required identity documents are missing." });
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "Quote failed");
        toast.error(msg);
      }
    }
    finally { setQuoteLoading(false); }
  };

  // Re-quote whenever the member changes anything that affects price OR advances to Cost/Approve.
  useEffect(() => {
    if (step === 4 || step === 5) loadQuote();
    // eslint-disable-next-line
  }, [step, mailClassId, returnReceipt, color, doubleSided, doc?.id]);

  const identityMissing = !!identityReady && !identityReady.ready_for_mailing;

  const loadSignatureStatus = async () => {
    setSigState(SIG.CHECKING);
    try {
      const r = await api.get(`/documents/${doc.id}/signature-status`);
      setSigStatus({ signature_name: r.data.signature_name || "", profile_name_ok: !!r.data.profile_name_ok });
      if (r.data.valid) {
        setSigInfo({ signature_name: r.data.signature_name, signature_consent_at: r.data.signature_consent_at });
        setSigState(SIG.SIGNED);
      } else {
        setSigInfo(null);
        setSigState(SIG.NEEDS_AUTH);
      }
    } catch {
      setSigState(SIG.STATUS_ERROR);
    }
  };

  const requireReauthorization = (message) => {
    setSigInfo(null); setConsentChecked(false); setPreview({ state: "idle" });
    setSignError(message);
    loadSignatureStatus();
  };

  const applySignatureAndPreview = async () => {
    if (!canApplySignature({ sigState, consentChecked, signatureName: sigStatus.signature_name })) return;
    setSigState(SIG.SIGNING); setSignError(null);
    const r = await applySignature(api, doc.id);
    if (!r.ok) { setSigState(SIG.SIGN_ERROR); setSignError(r.message); return; }
    setSigInfo({ signature_name: r.info.signature_name, signature_consent_at: r.info.signature_consent_at });
    setPreview({ state: "idle" });
    setSigState(SIG.SIGNED);
  };

  const loadSignedPreview = async () => {
    setPreview({ state: "loading" });
    const r = await fetchSignedPreview(api, doc.id);
    if (r.ok) { setPreview({ state: "loaded", url: URL.createObjectURL(r.blob) }); return; }
    if (r.kind === "unsigned") { requireReauthorization(r.message); return; }
    setPreview({ state: "error", kind: r.kind, message: r.message });
    if (r.kind === "missing") api.get("/identity-documents").then(x => setIdentityReady(x.data)).catch(() => { });
  };

  // A fresh signed preview is requested only after consent is confirmed valid.
  useEffect(() => {
    if (open && step === 2 && sigState === SIG.SIGNED && !identityMissing && preview.state === "idle") loadSignedPreview();
    // eslint-disable-next-line
  }, [open, step, sigState, identityMissing, preview.state]);

  useEffect(() => {
    const url = preview.url;
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [preview.url]);

  const submit = async ({ overrideAckCents } = {}) => {
    if (!authorized) return toast.error("Please provide explicit authorization");
    if (sigState !== SIG.SIGNED) return toast.error("Please authorize your typed electronic signature");
    if (!quote) return toast.error("Missing quote");
    const ackCents = Number.isFinite(overrideAckCents) ? overrideAckCents : quote.total_cents;
    // Client-side balance guard — the server is still authoritative. When the
    // user is accepting an updated price, this catches the price-exceeds-balance
    // case BEFORE we hit the server, so we never dispatch a request we know will
    // fail and we can show the insufficient-balance state cleanly.
    if (typeof quote.balance_cents === "number" && quote.balance_cents < ackCents) {
      setSendError({
        kind: "insufficient_balance",
        message: "The updated price is higher than your current Mail Credits balance.",
      });
      setPriceChange(null);
      return;
    }
    setSubmitting(true);
    setSendError(null);
    try {
      // Consent was recorded before the preview; the server re-validates the fingerprint here.
      const r = await api.post("/mailings/send", {
        document_id: doc.id, to_address: to, from_address: from,
        mail_type: selectedClass.mail_type, extra_service: resolvedExtraService,
        color, double_sided: doubleSided, idempotency_key: idem, authorized: true,
        identity_confirmed: identityConfirmed,
        acknowledged_price_cents: ackCents,
      });
      toast.success(`Letter submitted to Lob (${r.data.lob_id || "test"})`);
      setPriceChange(null);
      onSent && onSent(r.data); onOpenChange(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Structured 409 price_changed contract (2026-02-25). Detail is a dict:
      //   { code, message, previous_price_cents, new_price_cents, pages }
      // The idempotency_key was NOT consumed on this 409 path — the reservation
      // ledger write happens strictly after the price guard, and duplicate
      // protection at the DB layer guarantees a same-key retry becomes a replay,
      // not a duplicate mailing. So we reuse `idem` on the acceptance retry.
      if (status === 409 && detail && typeof detail === "object" && detail.code === "price_changed") {
        setPriceChange({
          previous_cents: Number(detail.previous_price_cents) || ackCents,
          new_cents: Number(detail.new_price_cents) || ackCents,
          pages: Number(detail.pages) || null,
        });
        // Refresh the quote so `total_cents` and `remaining_after_cents` reflect
        // the new server-authoritative price the user is being asked to accept.
        loadQuote();
        setSendError({
          kind: "price_changed",
          message: detail.message || "The mailing price changed since your review.",
        });
      } else if (status === 422 && detail && typeof detail === "object" && detail.code === "missing_identity") {
        setSendError({
          kind: "missing_identity",
          message: detail.message || "Required identity documents are missing.",
        });
      } else if (status === 422 && detail && typeof detail === "object" && SIGNATURE_SEND_ERROR_CODES.includes(detail.code)) {
        // Backend refused before any credit movement; send the member back to re-authorize.
        requireReauthorization(detail.message || "Please authorize your typed electronic signature before mailing.");
        setStep(2);
      } else if (status === 422 && typeof detail === "string" && /identity/i.test(detail)) {
        setSendError({ kind: "missing_identity", message: detail });
      } else if ((status === 400 || status === 402) && typeof detail === "string" && /balance|credits/i.test(detail)) {
        setSendError({ kind: "insufficient_balance", message: detail });
      } else if (status === 422 && typeof detail === "string" && /address|deliverab|zip/i.test(detail)) {
        setSendError({ kind: "address", message: detail });
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "We couldn’t submit this letter. Please try again.");
        setSendError({ kind: "technical", message: msg });
      }
    } finally { setSubmitting(false); }
  };

  const acceptUpdatedPrice = () => {
    if (!priceChange) return;
    // Balance re-check is done inside submit() using the updated ack amount.
    submit({ overrideAckCents: priceChange.new_cents });
  };

  const goTopUp = () => {
    localStorage.setItem("iat_return_to_mail", "/app/documents");
    onOpenChange(false);
    nav("/app/credits");
  };

  const next = () => {
    if (step === 0 && (!to.name || !to.address_line1 || !to.city || !to.state || !to.zip_code)) return toast.error("Complete recipient address");
    if (step === 1 && (!from.name || !from.address_line1 || !from.city || !from.state || !from.zip_code)) return toast.error("Complete return address");
    setStep(s => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep(s => Math.max(0, s - 1));

  const previewUrl = doc ? `${API}/documents/${doc.id}/download?fmt=pdf` : "";

  // Mobile layout (2026-02-25):
  //   The Radix DialogContent default is a centered fixed box that grows with
  //   its content. On iPhone Safari that pushes the DialogFooter beneath the
  //   dynamic bottom toolbar and users can't reach Next/Continue. The overrides
  //   below give the dialog a full-height mobile shell with three regions:
  //     - Sticky header (title + step-progress)
  //     - Independently scrollable body (min-h-0 + overflow-y-auto)
  //     - Sticky footer (Back / Next / Submit) with env(safe-area-inset-bottom)
  //   Desktop presentation (sm and up) is unchanged: centered rounded modal.
  const mobileShell = [
    "w-screen h-[100dvh] max-w-none max-h-none rounded-none",
    "top-0 left-0 translate-x-0 translate-y-0",
    "sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-3xl sm:rounded-lg",
    "sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2",
    "p-0 gap-0 flex flex-col overflow-hidden bg-white text-slate-900",
  ].join(" ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={mobileShell}
        data-testid="mail-letter-dialog"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 sm:px-6 pt-4 pb-3">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Mail size={18} className="text-purple-600" />
              Mail this letter — {STEPS[step]}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Six-step guided flow to mail this dispute letter through Lob: recipient, return address, PDF preview, mail-class options, cost breakdown, and final approval.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-6 gap-1 mt-3" data-testid="mail-progress">
            {STEPS.map((s, i) => (
              <div key={s} className={`h-1.5 rounded-full ${i <= step ? "bg-purple-500" : "bg-slate-200"}`}></div>
            ))}
          </div>
        </div>

        <div
          ref={bodyRef}
          className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-3"
          data-testid="mail-dialog-body"
        >
          {sendError && sendError.kind !== "price_changed" && (
            <SendErrorBanner error={sendError} onDismiss={() => setSendError(null)} />
          )}

          {step === 0 && doc?.letter_type === CFPB_LETTER_TYPE && (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5" data-testid="mail-cfpb-blocked">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-amber-700 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-900">CFPB complaints are filed online — not by mail</div>
                  <p className="text-xs text-amber-900 mt-1">The Consumer Financial Protection Bureau requires complaints to be submitted through its online portal at consumerfinance.gov. Use the CFPB Builder in this app to prepare and submit your complaint.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button onClick={() => { onOpenChange(false); nav("/app/cfpb"); }} className="rounded-xl btn-primary min-h-[44px]" data-testid="mail-cfpb-goto-builder">Open CFPB Builder</Button>
                    <Button onClick={() => onOpenChange(false)} variant="outline" className="rounded-xl min-h-[44px]" data-testid="mail-cfpb-close">Close</Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 0 && doc?.letter_type !== CFPB_LETTER_TYPE && NON_BUREAU_LETTER_TYPES.includes(doc?.letter_type) && (
            <div className="rounded-2xl border border-sky-300 bg-sky-50 p-4" data-testid="mail-non-bureau-explainer">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-sky-700 mt-0.5" />
                <div className="text-sm text-sky-900">
                  <div className="font-semibold">This letter is mailed to the furnisher / creditor</div>
                  <p className="text-xs mt-1">
                    {to.name
                      ? <>We’ve pre-filled the recipient’s name from your dispute selection. Please enter their mailing address below. If you don’t have it, look at a recent billing statement or your credit report.</>
                      : <>Enter the furnisher’s or creditor’s mailing address below. You’ll find it on a recent billing statement, letter, or credit report.</>}
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 0 && doc?.letter_type !== CFPB_LETTER_TYPE && (
            <div className="space-y-3">
              <AddressForm value={to} onChange={setTo} testidPrefix="mail-to" />
              <div className="flex flex-wrap gap-2">
                <Button onClick={verifyTo} disabled={toVerifying} className="btn-primary rounded-xl min-h-[44px]" data-testid="mail-verify-to-address">
                  {toVerifying ? <><Loader2 className="animate-spin mr-2" size={14} />Verifying…</> : "Verify recipient with Lob"}
                </Button>
                {toVerification?.recommended && <Button variant="outline" onClick={acceptRecommendedTo} className="rounded-xl min-h-[44px]" data-testid="mail-accept-to-recommended">Use recommended</Button>}
              </div>
              {toVerification && (
                <div className={`rounded-xl p-3 border text-xs ${toVerification.deliverability === "deliverable" ? "badge-success" : "badge-warning"}`} data-testid="mail-to-verification-panel">
                  <b>Deliverability:</b> {toVerification.deliverability} · <b>Line:</b> {toVerification.primary_line}, {toVerification.last_line}
                  {toVerification.deliverability !== "deliverable" && <div className="mt-1 text-slate-700">Lob may reject this letter at print time. Click <b>Use recommended</b> if a normalized suggestion is offered, or edit the address and re-verify.</div>}
                </div>
              )}
              <p className="text-[11px] text-slate-500">Tip: verify the recipient here to catch USPS-deliverability rejections before you approve mailing.</p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <AddressForm value={from} onChange={setFrom} testidPrefix="mail-from" />
              <div className="flex flex-wrap gap-2">
                <Button onClick={verifyReturn} disabled={verifying} className="btn-primary rounded-xl min-h-[44px]" data-testid="mail-verify-address">
                  {verifying ? <><Loader2 className="animate-spin mr-2" size={14} />Verifying…</> : "Verify address with Lob"}
                </Button>
                {fromVerification?.recommended && <Button variant="outline" onClick={acceptRecommendedFrom} className="rounded-xl min-h-[44px]" data-testid="mail-accept-recommended">Use recommended</Button>}
              </div>
              {fromVerification && (
                <div className={`rounded-xl p-3 border text-xs ${fromVerification.deliverability === "deliverable" ? "badge-success" : "badge-warning"}`} data-testid="mail-verification-panel">
                  <b>Deliverability:</b> {fromVerification.deliverability} · <b>Line:</b> {fromVerification.primary_line}, {fromVerification.last_line}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2">
              {identityMissing ? (
                <IdentityChecklistPanel identityReady={identityReady} docId={doc.id} onOpenChange={onOpenChange} nav={nav} />
              ) : sigState !== SIG.SIGNED ? (
                <SignatureAuthorizationPanel
                  sigState={sigState}
                  signatureName={sigStatus.signature_name}
                  profileNameOk={sigStatus.profile_name_ok}
                  consentChecked={consentChecked}
                  onConsentChange={(v) => { setConsentChecked(v); if (v && sigState !== SIG.SIGN_ERROR) setSignError(null); }}
                  onApply={applySignatureAndPreview}
                  onRetryStatus={loadSignatureStatus}
                  signError={signError}
                />
              ) : (
                <>
                  <SignedConfirmation name={sigInfo?.signature_name} signedAt={sigInfo?.signature_consent_at} />
                  <p className="text-sm text-slate-600">Final print-ready PDF preview — this is the exact signed packet (letter + your identity documents) that Lob will print and mail.</p>
                  <PacketPreviewView preview={preview} onRetry={loadSignedPreview} />
                </>
              )}
              <p className="text-[11px] text-slate-500">Order: dispute letter → photo ID front → photo ID back (if uploaded) → proof of address.</p>
              <p className="text-[11px] text-slate-500">Previewing does not deduct mailing credits and does not create a Lob mail piece.</p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="mail-options-step">
              <div>
                <Label className="text-slate-700 text-xs">Choose mail class</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  {MAIL_CLASSES.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setMailClassId(c.id)}
                      data-testid={`mail-class-${c.id}`}
                      className={`text-left rounded-2xl border p-4 transition min-h-[44px] ${mailClassId === c.id ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                    >
                      <div className="font-semibold text-slate-900">{c.label}</div>
                      <div className="text-xs text-purple-700 mt-0.5">{c.startingAt}</div>
                      <p className="text-xs text-slate-600 mt-2">{c.tagline}</p>
                    </button>
                  ))}
                </div>
              </div>

              {mailClassId === "certified" && (
                <label className="flex items-start gap-2 text-sm text-slate-800 rounded-xl border border-slate-200 p-3 bg-slate-50" data-testid="mail-return-receipt-row">
                  <Checkbox checked={returnReceipt} onCheckedChange={(v) => setReturnReceipt(!!v)} data-testid="mail-return-receipt" />
                  <div>
                    <div className="font-medium">Add Electronic Return Receipt — +$2.91</div>
                    <div className="text-xs text-slate-600">USPS emails you a signed proof of delivery once the recipient signs.</div>
                  </div>
                </label>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-800"><Checkbox checked={color} onCheckedChange={(v) => setColor(!!v)} data-testid="mail-color" /> Color printing</label>
                <label className="flex items-center gap-2 text-sm text-slate-800"><Checkbox checked={doubleSided} onCheckedChange={(v) => setDoubleSided(!!v)} data-testid="mail-doublesided" /> Double-sided</label>
              </div>
              <p className="text-[11px] text-slate-500">Certified Mail options are only available with USPS First-Class.</p>
            </div>
          )}

          {step === 4 && (
            <CostPanel
              quote={quote}
              loading={quoteLoading}
              expanded={showBreakdownCost}
              onToggle={() => setShowBreakdownCost(v => !v)}
              onTopUp={goTopUp}
            />
          )}

          {step === 5 && quote && (
            <div className="space-y-3">
              {priceChange && (
                <PriceChangedPanel
                  priceChange={priceChange}
                  balanceCents={quote.balance_cents}
                  submitting={submitting}
                  onAccept={acceptUpdatedPrice}
                  onDismiss={() => setPriceChange(null)}
                />
              )}
              <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
                <div className="flex items-center gap-2 text-slate-900 font-semibold"><ShieldCheck size={16} className="text-purple-600" />Final review</div>
                <div className="mt-2 text-sm text-slate-700 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><b>To:</b> {to.name}, {to.address_line1}, {to.city} {to.state} {to.zip_code}</div>
                  <div><b>From:</b> {from.name}, {from.address_line1}, {from.city} {from.state} {from.zip_code}</div>
                  <div><b>Mail class:</b> {selectedClass.label}</div>
                  <div><b>Return receipt:</b> {returnReceipt ? "Yes" : "No"}</div>
                  <div><b>Pages / sheets:</b> {quote.pages} pages · {quote.sheets} sheet{quote.sheets === 1 ? "" : "s"}</div>
                  <div><b>Printing:</b> {color ? "Color" : "Black & white"}{doubleSided ? ", double-sided" : ", single-sided"}</div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-5 bg-white" data-testid="mail-approve-total">
                <div className="text-xs text-purple-700 uppercase tracking-widest">Total mailing cost</div>
                <div className="text-3xl font-semibold text-slate-900 mt-1" data-testid="mail-approve-price">{money(quote.total_cents)}</div>
                <button
                  type="button"
                  onClick={() => setShowBreakdownApprove(v => !v)}
                  className="mt-2 text-xs text-purple-700 hover:underline inline-flex items-center gap-1 min-h-[44px]"
                  data-testid="mail-approve-toggle-breakdown"
                >
                  {showBreakdownApprove ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  {showBreakdownApprove ? "Hide price breakdown" : "See price breakdown"}
                </button>
                {showBreakdownApprove && <BreakdownList lines={quote.lines} total={quote.total_cents} />}
                <div className="mt-3 text-xs text-slate-600 flex items-center gap-1">
                  <Coins size={12} className="text-purple-600" />
                  Balance after mailing: <b className={quote.sufficient ? "text-emerald-700" : "text-red-700"} data-testid="mail-approve-balance-after">{money(quote.remaining_after_cents)}</b>
                </div>
              </div>

              {options?.lob_mode === "test" && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">Lob is in <b>test mode</b>. Credits will still be deducted for testing; this letter will NOT be printed or mailed.</div>
              )}
              <label className="flex items-start gap-2 text-sm text-slate-800">
                <Checkbox checked={identityConfirmed} onCheckedChange={(v) => setIdentityConfirmed(!!v)} data-testid="mail-identity-confirm" />
                I confirm that these identity documents belong to me, are accurate and readable, and may be printed and mailed with this dispute letter.
              </label>

              {sigInfo && <SignedConfirmation name={sigInfo.signature_name} signedAt={sigInfo.signature_consent_at} testid="mail-signature-summary" />}

              <label className="flex items-start gap-2 text-sm text-slate-800">
                <Checkbox checked={authorized} onCheckedChange={(v) => setAuthorized(!!v)} data-testid="mail-authorize" />
                I approve mailing this letter and authorize deduction of <b>{money(quote.total_cents)}</b> from my Mail Credits.
              </label>
            </div>
          )}

          {/* Bottom spacer — guarantees the last piece of content is never
              obscured by the sticky footer even when the on-screen keyboard is
              open on iOS Safari. */}
          <div aria-hidden className="h-6" />
        </div>

        {(() => {
          const isCfpb = doc?.letter_type === CFPB_LETTER_TYPE;
          const recipientIncomplete = step === 0 && !isCfpb && (
            !to.name?.trim() || !to.address_line1?.trim() || !to.city?.trim() || !to.state?.trim() || !to.zip_code?.trim()
          );
          const previewIdentityMissing = step === 2 && identityMissing;
          const signedPreviewPending = step === 2 && !identityMissing && !canProceedFromPreview({ identityMissing, sigState, previewState: preview.state });
          const blockNext = isCfpb || previewIdentityMissing || signedPreviewPending || recipientIncomplete;
          const nextClick = blockNext ? undefined : next;
          const submitDisabled = isSubmitDisabled({ submitting, authorized, sigState, identityConfirmed, sufficient: quote?.sufficient, priceChange });
          return (
            <div
              className="sticky bottom-0 z-10 bg-white border-t border-slate-200 px-4 sm:px-6 pt-3"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
              data-testid="mail-dialog-footer"
            >
              {signedPreviewPending && (
                <div className="mb-2 text-xs text-purple-900 bg-purple-50 border border-purple-200 rounded-lg p-2" data-testid="mail-next-disabled-signature">
                  Apply your signature and wait for the signed preview to load to continue.
                </div>
              )}
              {previewIdentityMissing && (
                <div
                  className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2"
                  data-testid="mail-next-disabled-missing-identity"
                >
                  Upload the required identity documents to continue. Use <b>Upload missing documents</b> above.
                </div>
              )}
              {recipientIncomplete && (
                <div
                  className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2"
                  data-testid="mail-next-disabled-recipient-incomplete"
                >
                  Enter the recipient’s full mailing address (name, line 1, city, state, ZIP) to continue.
                </div>
              )}
              <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
                <div className="flex-shrink-0">
                  {step > 0 && (
                    <Button
                      variant="outline"
                      onClick={back}
                      className="min-h-[44px] rounded-xl"
                      data-testid="mail-back"
                    >
                      <ArrowLeft size={14} className="mr-1" />Back
                    </Button>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {step < STEPS.length - 1 && (
                    <Button
                      onClick={nextClick}
                      disabled={blockNext}
                      aria-disabled={blockNext || undefined}
                      className="btn-primary rounded-xl min-h-[44px]"
                      data-testid="mail-next"
                    >
                      Next<ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                  {step === STEPS.length - 1 && (
                    <Button
                      onClick={() => submit()}
                      disabled={submitDisabled}
                      aria-disabled={submitDisabled || undefined}
                      className="btn-primary rounded-xl min-h-[44px]"
                      data-testid="mail-submit"
                    >
                      {submitting ? <><Loader2 className="animate-spin mr-2" size={14} />Sending…</> : <><Check size={14} className="mr-1" />Approve and Mail</>}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Price-changed acceptance panel (2026-02-25) ----------
function PriceChangedPanel({ priceChange, balanceCents, submitting, onAccept, onDismiss }) {
  const prev = Number(priceChange?.previous_cents) || 0;
  const next = Number(priceChange?.new_cents) || 0;
  const delta = next - prev;
  const affordable = typeof balanceCents === "number" ? balanceCents >= next : true;
  const remaining = typeof balanceCents === "number" ? balanceCents - next : null;
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:p-5" data-testid="mail-price-changed-panel">
      <div className="flex items-start gap-2">
        <AlertTriangle size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-amber-900">The mailing price changed</div>
          <p className="text-xs text-amber-900 mt-1">
            {priceChange?.pages
              ? `Your final packet is ${priceChange.pages} page${priceChange.pages === 1 ? "" : "s"} — this may include identity attachments or a longer draft that affects postage.`
              : "This is usually because the final packet page count changed after your identity documents were attached."}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg bg-white border border-amber-200 p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Previous</div>
              <div className="font-semibold text-slate-900 mt-1" data-testid="mail-price-changed-previous">${(prev / 100).toFixed(2)}</div>
            </div>
            <div className="rounded-lg bg-white border border-amber-300 p-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-700">Updated</div>
              <div className="font-semibold text-amber-900 mt-1" data-testid="mail-price-changed-new">${(next / 100).toFixed(2)}</div>
            </div>
          </div>
          <div className="mt-2 text-xs text-amber-900" data-testid="mail-price-changed-delta">
            Difference: <b>{delta >= 0 ? "+" : ""}${(delta / 100).toFixed(2)}</b>
          </div>
          {typeof balanceCents === "number" && (
            <div className="mt-2 text-xs text-amber-900">
              Available balance: <b>${(balanceCents / 100).toFixed(2)}</b>
              {affordable
                ? <> · Balance after mailing: <b>${((remaining ?? 0) / 100).toFixed(2)}</b></>
                : <> · <b className="text-red-700">Insufficient — top up first</b></>}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              onClick={onAccept}
              disabled={submitting || !affordable}
              aria-disabled={submitting || !affordable || undefined}
              className="btn-primary rounded-xl min-h-[44px]"
              data-testid="mail-accept-updated-price"
            >
              {submitting ? <><Loader2 className="animate-spin mr-2" size={14} />Submitting…</> : "Accept updated price and mail"}
            </Button>
            <Button
              variant="outline"
              onClick={onDismiss}
              disabled={submitting}
              className="rounded-xl min-h-[44px]"
              data-testid="mail-price-changed-dismiss"
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Send error banner (distinguishes error kinds) ----------
function SendErrorBanner({ error, onDismiss }) {
  const styles = {
    price_changed: { bg: "bg-amber-50 border-amber-300", text: "text-amber-900", label: "Price changed" },
    insufficient_balance: { bg: "bg-red-50 border-red-200", text: "text-red-800", label: "Insufficient balance" },
    address: { bg: "bg-amber-50 border-amber-200", text: "text-amber-900", label: "Address problem" },
    missing_identity: { bg: "bg-amber-50 border-amber-200", text: "text-amber-900", label: "Identity documents required" },
    technical: { bg: "bg-red-50 border-red-200", text: "text-red-800", label: "Submission failed" },
  };
  const s = styles[error?.kind] || styles.technical;
  return (
    <div
      className={`rounded-xl border p-3 ${s.bg} ${s.text}`}
      data-testid={`mail-error-${error.kind}`}
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <div className="font-semibold">{s.label}</div>
          <div className="text-xs mt-1">{error.message}</div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-xs underline min-h-[44px] px-2"
          data-testid={`mail-error-dismiss-${error.kind}`}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function IdentityChecklistPanel({ identityReady, docId, onOpenChange, nav }) {
  const missing = [];
  if (!identityReady?.id_front) missing.push({ id: "id_front", label: "Photo ID — Front" });
  if (!identityReady?.proof_of_address) missing.push({ id: "proof_of_address", label: "Proof of Address" });
  const goUpload = () => {
    try { if (docId) localStorage.setItem("iat_return_to_mail_doc", docId); } catch { /* storage disabled */ }
    onOpenChange(false);
    nav("/app/identity");
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="mail-identity-checklist">
      <div className="flex items-center gap-2 text-amber-900 font-semibold">
        <ShieldCheck size={16} className="text-amber-700" />
        Identity documents required before mailing
      </div>
      <p className="text-xs text-amber-900 mt-1">
        The mailing packet is <b>letter → photo ID front → photo ID back (if uploaded) → proof of address</b>.
        These items must be on file before we can build the preview or submit to Lob. Your dispute draft is saved
        and you'll return to this step after uploading.
      </p>
      <ul className="mt-3 space-y-1 text-sm">
        {[
          { id: "id_front", label: "Photo ID — Front", required: true, present: !!identityReady?.id_front },
          { id: "id_back", label: "Photo ID — Back (optional)", required: false, present: !!identityReady?.id_back },
          { id: "proof_of_address", label: "Proof of Address", required: true, present: !!identityReady?.proof_of_address },
        ].map((row) => (
          <li key={row.id} className="flex items-center gap-2" data-testid={`mail-identity-row-${row.id}`}>
            {row.present ? (
              <Check size={14} className="text-emerald-700" />
            ) : row.required ? (
              <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
            ) : (
              <span className="inline-block h-2 w-2 rounded-full bg-slate-300" />
            )}
            <span className={row.present ? "text-emerald-900" : row.required ? "text-amber-900" : "text-slate-700"}>
              {row.label}
              {row.present && <span className="ml-2 text-xs text-emerald-700">Uploaded</span>}
              {!row.present && row.required && <span className="ml-2 text-xs text-amber-700">Missing</span>}
              {!row.present && !row.required && <span className="ml-2 text-xs text-slate-500">Optional</span>}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={goUpload} className="btn-primary rounded-xl" data-testid="mail-upload-missing-docs">
          <ArrowRight size={14} className="mr-1" />
          Upload missing documents
        </Button>
      </div>
      <p className="text-[11px] text-amber-900 mt-2">
        {missing.length} required item{missing.length === 1 ? "" : "s"} missing. Previewing does not deduct mailing credits.
      </p>
    </div>
  );
}


function PacketPreviewView({ preview, onRetry }) {
  if (preview.state === "error") {
    const isMissing = preview.kind === "missing";
    return (
      <div
        className={`p-4 rounded-xl border ${isMissing ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-red-50 border-red-200 text-red-800"}`}
        data-testid={isMissing ? "mail-pdf-preview-missing" : "mail-pdf-preview-error"}
      >
        <div className="text-sm font-medium">{isMissing ? "Identity documents required" : "Signed packet preview failed"}</div>
        <div className="text-xs mt-1">{preview.message}</div>
        {!isMissing && (
          <Button variant="outline" onClick={onRetry} className="rounded-xl mt-3 min-h-[44px]" data-testid="mail-pdf-preview-retry">
            Retry preview
          </Button>
        )}
      </div>
    );
  }
  if (preview.state !== "loaded") {
    return <div className="p-4 text-xs text-slate-500 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-2" data-testid="mail-pdf-preview-loading"><Loader2 className="animate-spin" size={14} />Generating your signed packet…</div>;
  }
  return <iframe title="Signed mailing packet PDF" src={preview.url} className="w-full h-[55dvh] min-h-[280px] sm:h-[480px] rounded-xl border border-slate-200 bg-white" data-testid="mail-pdf-preview" />;
}


function CostPanel({ quote, loading, expanded, onToggle, onTopUp }) {
  if (loading || !quote) {
    return (
      <div className="rounded-2xl border border-slate-200 p-6 bg-slate-50" data-testid="mail-cost-panel">
        <div className="text-slate-600 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} />Loading price…</div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200 p-6 bg-slate-50" data-testid="mail-cost-panel">
      <div className="text-xs text-purple-700 uppercase tracking-widest">Total mailing cost</div>
      <div className="text-3xl font-semibold text-slate-900 mt-1" data-testid="mail-price">{money(quote.total_cents)}</div>
      <div className="text-xs text-slate-600 mt-1">
        {quote.mail_type_label}{quote.extra_service_label ? ` · ${quote.extra_service_label}` : ""} · {quote.eta}
      </div>
      <div className="text-xs text-slate-600">
        {quote.pages} page{quote.pages === 1 ? "" : "s"} · {quote.sheets} sheet{quote.sheets === 1 ? "" : "s"} · {quote.color ? "Color" : "B&W"}{quote.double_sided ? ", double-sided" : ", single-sided"}
      </div>

      <button
        type="button"
        onClick={onToggle}
        className="mt-3 text-xs text-purple-700 hover:underline inline-flex items-center gap-1"
        data-testid="mail-cost-toggle-breakdown"
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {expanded ? "Hide price breakdown" : "See price breakdown"}
      </button>
      {expanded && <BreakdownList lines={quote.lines} total={quote.total_cents} />}

      <div className="mt-4 flex items-center justify-between text-xs text-slate-600">
        <span>Current balance</span>
        <span className="flex items-center gap-1"><Coins size={12} className="text-purple-600" />{money(quote.balance_cents)}</span>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600">Balance after mailing</span>
        <span className={quote.sufficient ? "text-emerald-700 font-medium" : "text-red-700 font-medium"}>{money(quote.remaining_after_cents)}</span>
      </div>

      {!quote.sufficient && (
        <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-800" data-testid="mail-insufficient">You need <b>{money(quote.shortfall_cents)}</b> more in Accelerator Mail Credits to mail this letter.</p>
          <Button onClick={onTopUp} className="btn-primary rounded-xl mt-3" data-testid="mail-goto-topup">Add Mail Credits<ArrowRight size={14} className="ml-1" /></Button>
        </div>
      )}
    </div>
  );
}

function BreakdownList({ lines, total }) {
  return (
    <ul className="mt-3 text-sm text-slate-700 space-y-1 border-t border-slate-200 pt-3" data-testid="mail-price-breakdown">
      {(lines || []).map((l) => (
        <li key={l.key} className="flex justify-between gap-3" data-testid={`mail-price-line-${l.key}`}>
          <span className="text-slate-600">{l.label}</span>
          <span className="font-medium">{money(l.cents)}</span>
        </li>
      ))}
      <li className="flex justify-between gap-3 border-t border-slate-200 pt-2 mt-1">
        <span className="font-semibold text-slate-900">Total mailing cost</span>
        <span className="font-semibold text-slate-900">{money(total)}</span>
      </li>
    </ul>
  );
}

function AddressForm({ value, onChange, testidPrefix }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2"><Label className="text-slate-700 text-xs">Name / Company</Label><Input value={value.name} onChange={(e) => set("name", e.target.value)} data-testid={`${testidPrefix}-name`} /></div>
      <div className="sm:col-span-2"><Label className="text-slate-700 text-xs">Address line 1</Label><Input value={value.address_line1} onChange={(e) => set("address_line1", e.target.value)} data-testid={`${testidPrefix}-line1`} /></div>
      <div className="sm:col-span-2"><Label className="text-slate-700 text-xs">Address line 2 (optional)</Label><Input value={value.address_line2} onChange={(e) => set("address_line2", e.target.value)} data-testid={`${testidPrefix}-line2`} /></div>
      <div><Label className="text-slate-700 text-xs">City</Label><Input value={value.city} onChange={(e) => set("city", e.target.value)} data-testid={`${testidPrefix}-city`} /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-slate-700 text-xs">State</Label><Input maxLength={2} value={value.state} onChange={(e) => set("state", e.target.value.toUpperCase())} data-testid={`${testidPrefix}-state`} /></div>
        <div><Label className="text-slate-700 text-xs">ZIP</Label><Input value={value.zip_code} onChange={(e) => set("zip_code", e.target.value)} data-testid={`${testidPrefix}-zip`} /></div>
      </div>
    </div>
  );
}
